/**
 * Inline advisory for a sign-in check that could not answer, with the retry
 * that re-runs it. The step-up check by default; a host whose failing check is
 * a different one names it through `message`.
 *
 * Shared by the web login page and the native sign-in sheet so the two say the
 * same thing about the same state. It renders no session semantics of its own -
 * the hook keeps the session alive precisely so this retry costs a tap rather
 * than the whole credential again, and every dismissal route already tears that
 * session down.
 */

import { useTranslation } from 'react-i18next';

import { AUTH_LINK_CLASS } from '@/components/auth/auth-styles';

import type { Ref } from 'react';

interface MfaCheckFailedNoticeProps {
  /** Whether the check actually failed. See the live-region note below. */
  readonly show: boolean;
  readonly onRetry: () => void;
  /** What could not be answered, when it was not the step-up check itself. */
  readonly message?: string;
  /** Chrome for the host surface; the two callers sit on different paper. */
  readonly className?: string;
  /**
   * Handle for a host that has to move focus here. Dismissing the notice
   * unmounts the retry button the click came from, so focus needs somewhere
   * deliberate to land rather than falling to `<body>`.
   */
  readonly ref?: Ref<HTMLParagraphElement>;
}

export function MfaCheckFailedNotice({
  show,
  onRetry,
  message,
  className = '',
  ref,
}: MfaCheckFailedNoticeProps) {
  const { t } = useTranslation('auth');

  return (
    // Mounted unconditionally, and never `display: none`: a live region
    // introduced at the same time as its content is not reliably announced.
    // `tabIndex={-1}` keeps it out of the tab order while leaving it a legal
    // programmatic focus target for that handover.
    <p
      ref={ref}
      aria-live="polite"
      className={className}
      data-testid="mfa-check-failed-notice"
      tabIndex={-1}
    >
      {show ? (
        <>
          {message ?? t('passwordSignIn.stepUpCheckFailed')}
          <button className={`${AUTH_LINK_CLASS} ml-1.5`} type="button" onClick={onRetry}>
            {t('passwordSignIn.stepUpRetry')}
          </button>
        </>
      ) : null}
    </p>
  );
}
